import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { retrieveToken } from "../../../utils/storage";

// Define a service using a base URL and expected endpoints
export const adminFolderApi = createApi({
  reducerPath: "adminFolder",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["folders", "users", "documents"],
  endpoints: (builder) => ({
    getUsersFolder: builder.query({
      query: ({
        page: { startRow, endRow },
        query = "",
        accessToken = "",
        extraFilters = {},
        orderDirection = "desc",
        valueToOrderBy = "lastDocUploadedAt",
      }) => ({
        url: `admin/users/list?query=${query}&withFolders=true`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken || retrieveToken()}`,
        },
        body: {
          startRow,
          endRow,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
          filter: {
            ...extraFilters,
          },
        },
      }),
      providesTags: ["users", "folders"],
    }),
    getDocumentsFolder: builder.query({
      query: ({
        page: { startRow, endRow },
        accessToken = "",
        folderId = "",
        orderDirection = "desc",
        valueToOrderBy = "createdAt",
      }) => ({
        url: `admin/Folder/${folderId}/documents/list`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken || retrieveToken()}`,
        },
        body: {
          startRow,
          endRow,
          folderId,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
        },
      }),
      providesTags: ["documents"],
    }),

    getAllUserDocumentsFolder: builder.query({
      query: ({
        folderId = "",
        query = "",
        page: { startRow, endRow },
        filter = {
          mainCategoryId: "",
          categoryId: "",
        },
        orderDirection = "desc",
        valueToOrderBy = "createdAt",
      }) => ({
        url: `admin/Folder/${folderId}/alluserdocuments?query=${query}&mainCategoryId=${filter.mainCategoryId}&categoryId=${filter.categoryId}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: {
          startRow,
          endRow,
          folderId,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
        },
      }),
      providesTags: ["documents"],
    }),
    getFoldersCustom: builder.query({
      query: ({ page: { startRow, endRow }, userId }) => ({
        url: `admin/Folder/list`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        params: { userId },
        body: { startRow, endRow },
      }),
      providesTags: ["folders"],
    }),
    getFolderMacro: builder.query({
      query: (userId) => ({
        url: `admin/users/${userId}/standardfolder/macrocat`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["folders"],
    }),
    doCreateFolder: builder.mutation({
      query: (body) => ({
        url: `/admin/Folder`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["folders"],
    }),

    doEditFolder: builder.mutation({
      query: (body) => ({
        url: `admin/Folder/${body.folderId}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["folders"],
    }),

    doAddDocumentFolder: builder.mutation({
      query: (body) => ({
        url: `admin/Folder/${body.folderId}/documents?documentId=${body.documentId}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["documents"],
    }),
    doDeleteFolder: builder.mutation({
      query: (folderId) => ({
        url: `admin/Folder/${folderId}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      invalidatesTags: ["folders"],
    }),
    doDeleteDocumentFolder: builder.mutation({
      query: (body) => ({
        url: `admin/Folder/${body.folderId}/documents/${body.documentId}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      invalidatesTags: ["folders", "documents"],
    }),
  }),
});

export const {
  useGetUsersFolderQuery,
  useGetFoldersCustomQuery,
  useGetDocumentsFolderQuery,
  useGetAllUserDocumentsFolderQuery,
  useDoAddDocumentFolderMutation,
  useGetFolderMacroQuery,
  useDoCreateFolderMutation,
  useDoEditFolderMutation,
  useDoDeleteFolderMutation,
  useDoDeleteDocumentFolderMutation,
} = adminFolderApi;
